// Vector art for Pashmak's bazaar items (port of ios/.../Character/OutfitArt.swift
// and android character/Outfits.kt). Coordinates are in the cat's design space:
// hats and glasses are relative to the head center, held items to their own
// center. Moving parts (steam, the goldfish, santur hammers, twinkling stars)
// are CSS animations in pashmak.css, so nothing here re-renders per frame.
//
// `u` is a per-instance prefix for gradient ids (many cats can share a page).

import type { ShopItem } from "../../lib/types";
import {
  C, HALO_PATTERN, boteh, crescent, crownShape, dome, khatam, steam, tapered, trapezoid, triangle, tulipGlass,
} from "./geometry";

type Slot = ShopItem["slot"];

// ---------------------------------------------------------------- scenes (halos)

export interface HaloTheme {
  colors: string[];
  pattern: string;
  center: string;
  rim: string;
  night?: boolean;
  roses?: boolean;
  blossoms?: boolean;
}

export function haloTheme(id: string): HaloTheme {
  switch (id) {
    case "halo.bahar":
      return { colors: ["#E4F6CF", C.pistachio, "#3E7C3A"], pattern: "rgba(255,255,255,.4)", center: "rgba(244,143,177,.5)", rim: C.gold, blossoms: true };
    case "halo.yalda":
      return { colors: ["#4A3A8A", "#1B1340", "#0B0820"], pattern: "rgba(242,160,7,.35)", center: "rgba(192,40,59,.45)", rim: C.pomegranate, night: true };
    case "halo.shiraz":
      return { colors: ["#FFE1E8", "#E57391", "#8E2A4B"], pattern: "rgba(255,255,255,.4)", center: "rgba(194,24,91,.35)", rim: C.gold, roses: true };
    case "halo.isfahan":
      return { colors: ["#FFF1C9", C.gold, C.lapis], pattern: "rgba(31,60,136,.45)", center: "rgba(27,165,168,.5)", rim: "#B8860B" };
    default:
      return { colors: [C.firuzeLight, C.firuze, C.lapis], pattern: "rgba(255,255,255,.35)", center: "rgba(242,160,7,.35)", rim: C.gold };
  }
}

function RadialStops({ colors, start }: { colors: string[]; start: number }) {
  return (
    <>
      {colors.map((c, i) => (
        <stop key={i} offset={start + ((1 - start) * i) / (colors.length - 1)} stopColor={c} />
      ))}
    </>
  );
}

export function Flower({ x, y, s, petal, center }: { x: number; y: number; s: number; petal: string; center: string }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const a = (i / 5) * 2 * Math.PI;
        return <circle key={i} cx={Math.cos(a) * s * 0.26} cy={Math.sin(a) * s * 0.26} r={s * 0.225} fill={petal} />;
      })}
      <circle r={s * 0.175} fill={center} />
    </g>
  );
}

function Rose({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle r={10} fill="#C2185B" />
      <circle cx={1} cy={-1} r={7} fill="#E91E63" />
      <circle r={4} fill="none" stroke="#880E4F" strokeWidth={1} />
    </g>
  );
}

/** Stars + moon, roses or blossoms over a themed halo. */
export function HaloDecor({ theme }: { theme: HaloTheme }) {
  return (
    <g>
      {theme.night && (
        <>
          {Array.from({ length: 14 }, (_, i) => {
            const a = i * 2.4;
            const r = 40 + ((i * 37) % 70);
            return (
              <circle
                key={i}
                className="pm-twinkle"
                style={{ animationDelay: `${-(i % 7) * 0.3}s` }}
                cx={Math.cos(a) * r}
                cy={Math.sin(a) * r}
                r={1.5}
                fill="#FFF3C4"
              />
            );
          })}
          <path d={crescent(-78, -70)} fill="#FFF3C4" />
        </>
      )}
      {theme.roses &&
        Array.from({ length: 6 }, (_, i) => {
          const a = (i / 6) * 2 * Math.PI + 0.3;
          return <Rose key={i} x={Math.cos(a) * 100} y={Math.sin(a) * 100} />;
        })}
      {theme.blossoms &&
        Array.from({ length: 8 }, (_, i) => {
          const a = (i / 8) * 2 * Math.PI;
          return (
            <g key={i} transform={`translate(${Math.cos(a) * 102} ${Math.sin(a) * 102})`}>
              <g className="pm-spin-slow">
                <Flower x={0} y={0} s={16} petal="#F8BBD0" center={C.saffron} />
              </g>
            </g>
          );
        })}
    </g>
  );
}

/** The tiled disc behind Pashmak (250 across). */
export function Halo({ id, u }: { id: string; u: string }) {
  const t = haloTheme(id);
  return (
    <g className="pm-halo">
      <defs>
        <radialGradient id={`${u}-halo`} gradientUnits="userSpaceOnUse" cx={0} cy={0} r={125}>
          <RadialStops colors={t.colors} start={0.08} />
        </radialGradient>
      </defs>
      <circle r={125} fill={`url(#${u}-halo)`} />
      <g className="pm-halo-spin">
        <path d={HALO_PATTERN} fill="none" stroke={t.pattern} strokeWidth={1} />
        <path d={khatam(0, 0, 44)} fill={t.center} />
      </g>
      <HaloDecor theme={t} />
      <circle r={122.5} fill="none" stroke={t.rim} strokeWidth={5} />
      <circle r={116.25} fill="none" stroke="rgba(255,255,255,.6)" strokeWidth={1.5} />
    </g>
  );
}

// ---------------------------------------------------------------- hats

const FLOWER_COLORS = ["#F48FB1", "#FFFFFF", "#FFD54F", "#CE93D8"];

export function HatArt({ id, u }: { id: string | null | undefined; u: string }) {
  switch (id) {
    case "hat.flowers":
      return (
        <g>
          {Array.from({ length: 7 }, (_, i) => {
            const a = Math.PI + ((i + 1) / 8) * Math.PI;
            return (
              <g key={i}>
                <ellipse
                  rx={6}
                  ry={3}
                  fill={C.pistachio}
                  transform={`translate(${Math.cos(a + 0.2) * 70} ${Math.sin(a + 0.2) * 60 - 6}) rotate(${((a + Math.PI / 2) * 180) / Math.PI})`}
                />
                <Flower x={Math.cos(a) * 70} y={Math.sin(a) * 60 - 6} s={22} petal={FLOWER_COLORS[i % 4]} center={i % 2 === 0 ? C.saffron : C.pomegranate} />
              </g>
            );
          })}
        </g>
      );
    case "hat.namadi":
      return (
        <g>
          <defs>
            <linearGradient id={`${u}-namadi`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#A1887F" />
              <stop offset="1" stopColor="#795548" />
            </linearGradient>
          </defs>
          <ellipse cy={-52} rx={59} ry={9} fill="#6D4C41" />
          <path d={dome(100, 54, 0, -80)} fill={`url(#${u}-namadi)`} />
          <path d={dome(84, 44, 0, -76)} fill="none" stroke="rgba(215,204,200,.7)" strokeWidth={1.5} strokeDasharray="3 3" />
        </g>
      );
    case "hat.darvish":
      return (
        <g>
          <defs>
            <linearGradient id={`${u}-darvish`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#E3CBA0" />
              <stop offset="1" stopColor="#B89468" />
            </linearGradient>
          </defs>
          <path d={tapered(62, 92, 0, -94)} fill={`url(#${u}-darvish)`} />
          <rect x={-33} y={-57} width={66} height={10} rx={5} fill="#8D6E4A" />
        </g>
      );
    case "hat.crown":
      return (
        <g>
          <defs>
            <linearGradient id={`${u}-crown`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#FFE082" />
              <stop offset=".5" stopColor="#D4A94F" />
              <stop offset="1" stopColor="#B8860B" />
            </linearGradient>
          </defs>
          <path d={crownShape(96, 58, 0, -88)} fill={`url(#${u}-crown)`} stroke="#8D6E00" strokeWidth={1.5} strokeLinejoin="round" />
          {[-2, -1, 0, 1, 2].map((i) => (
            <circle key={i} cx={i * 17} cy={-68} r={4} fill={i % 2 === 0 ? C.firuze : C.pomegranate} />
          ))}
          <g transform="translate(30 -112)">
            <path className="pm-pulse" d={khatam(0, 0, 8)} fill="rgba(255,255,255,.9)" />
          </g>
        </g>
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------- glasses

export function GlassesArt({ id, u }: { id: string | null | undefined; u: string }) {
  switch (id) {
    case "face.round":
      return (
        <g>
          {[-30, 30].map((x) => (
            <g key={x}>
              <circle cx={x} cy={-4} r={19} fill="rgba(255,255,255,.15)" />
              <circle cx={x} cy={-4} r={17.5} fill="none" stroke="#B8860B" strokeWidth={3} />
            </g>
          ))}
          <rect x={-12} y={-9.5} width={24} height={3} rx={1.5} fill="#B8860B" />
        </g>
      );
    case "face.sun":
      return (
        <g>
          <defs>
            <linearGradient id={`${u}-sun`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#1A1A2E" />
              <stop offset="1" stopColor={C.lapis} />
            </linearGradient>
          </defs>
          {[-30, 30].map((x) => (
            <g key={x}>
              <rect x={x - 21} y={-20} width={42} height={32} rx={12} fill={`url(#${u}-sun)`} />
              <rect x={-6} y={-2} width={12} height={4} rx={2} fill="rgba(255,255,255,.6)" transform={`translate(${x - 8} -12) rotate(-25)`} />
            </g>
          ))}
          <rect x={-10} y={-14} width={20} height={4} rx={2} fill="#1A1A2E" />
        </g>
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------- scarves

export function scarfColors(id: string): [string, string] {
  switch (id) {
    case "neck.anar":
      return [C.pomegranate, "#7A1020"];
    case "neck.saffron":
      return [C.saffron, "#D17A00"];
    case "neck.pesteh":
      return [C.pistachio, "#5E8C3A"];
    case "neck.termeh":
      return ["#9C2A4A", "#4A0E24"];
    default:
      return [C.firuze, C.lapis];
  }
}

/** The scarf band + boteh pendant. `big` is the shop-thumbnail variant (110×30). */
export function ScarfArt({ id, u, big }: { id: string; u: string; big?: boolean }) {
  const [a, b] = scarfColors(id);
  const termeh = id === "neck.termeh";
  const w = big ? 110 : 100;
  const h = big ? 30 : 22;
  const gid = `${u}-scarf${big ? "-big" : ""}`;
  return (
    <g>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={a} />
          <stop offset="1" stopColor={b} />
        </linearGradient>
      </defs>
      <rect x={-w / 2} y={-h / 2} width={w} height={h} rx={h / 2} fill={`url(#${gid})`} />
      {termeh
        ? [0, 1, 2, 3].map((i) => {
            const bw = big ? 9 : 8;
            const gap = big ? 10 : 8;
            const x = (i - 1.5) * (bw + gap);
            return <path key={i} d={boteh(bw, big ? 13 : 12, x, 0)} fill={C.gold} />;
          })
        : [0, 1, 2, 3, 4, 5].map((i) => {
            const d = big ? 4 : 3;
            const gap = big ? 10 : 9;
            return <circle key={i} cx={(i - 2.5) * (d + gap)} r={d / 2} fill={C.gold} opacity={big ? 1 : 0.9} />;
          })}
      {big ? (
        <path d={boteh(18, 26, 0, 24)} fill={C.saffron} />
      ) : (
        <path d={boteh(16, 22, 0, 16)} fill={C.saffron} stroke={C.pomegranate} strokeWidth={1.5} />
      )}
    </g>
  );
}

// ---------------------------------------------------------------- held items / friends

export function HeldArt({ id, u }: { id: string | null | undefined; u: string }) {
  switch (id) {
    case "held.chai":
      return (
        <g>
          <defs>
            <linearGradient id={`${u}-tea`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#C0561C" />
              <stop offset="1" stopColor="#7A2E0B" />
            </linearGradient>
            <clipPath id={`${u}-teacl`}>
              <rect x={-20} y={-12} width={40} height={34} />
            </clipPath>
          </defs>
          <ellipse cy={24} rx={26} ry={6} fill="#F5F5F5" stroke={C.gold} strokeWidth={1.5} />
          <path d={tulipGlass(30, 44)} fill="rgba(255,255,255,.5)" />
          <path d={tulipGlass(30, 44)} fill={`url(#${u}-tea)`} clipPath={`url(#${u}-teacl)`} />
          <path d={tulipGlass(30, 44)} fill="none" stroke="rgba(255,255,255,.9)" strokeWidth={1.5} />
          {[0, 1].map((i) => (
            <g key={i} transform={`translate(${i * 10 - 5} -34)`}>
              <path className="pm-steam" style={{ animationDelay: `${-i * 0.83}s` }} d={steam(10, 22)} fill="none" stroke="rgba(255,255,255,.8)" strokeWidth={2.5} strokeLinecap="round" />
            </g>
          ))}
        </g>
      );
    case "held.anar":
      return (
        <g>
          <defs>
            <radialGradient id={`${u}-anar`} cx=".35" cy=".35" r=".65">
              <stop offset="0" stopColor="#E53950" />
              <stop offset=".5" stopColor={C.pomegranate} />
              <stop offset="1" stopColor="#7A1020" />
            </radialGradient>
          </defs>
          <path d={crownShape(18, 12, 0, -24)} fill="#8E1B2C" />
          <ellipse rx={23} ry={22} fill={`url(#${u}-anar)`} />
          <ellipse rx={5} ry={3} fill="rgba(255,255,255,.35)" transform="translate(-10 -10) rotate(-30)" />
        </g>
      );
    case "held.fish":
      return (
        <g>
          <defs>
            <clipPath id={`${u}-water`}>
              <rect x={-30} y={-6} width={60} height={36} />
            </clipPath>
          </defs>
          <circle r={29} fill="rgba(189,232,245,.5)" />
          <circle r={29} fill="rgba(91,192,222,.55)" clipPath={`url(#${u}-water)`} />
          <g transform="translate(0 10)">
            <g className="pm-fish-y">
              <g className="pm-fish-x">
                <g className="pm-fish-dir">
                  <ellipse rx={9} ry={5.5} fill="#FF8A00" />
                  <path d={triangle(9, 11)} fill="#FF6F00" transform="translate(-12 0) rotate(-90)" />
                  <circle cx={5} cy={-1} r={1.25} fill="#000" />
                </g>
              </g>
            </g>
          </g>
          <circle r={28} fill="none" stroke="rgba(255,255,255,.9)" strokeWidth={2} />
          <rect x={-6} y={-2} width={12} height={4} rx={2} fill="rgba(255,255,255,.6)" transform="translate(-16 -16) rotate(-40)" />
        </g>
      );
    case "held.hafez":
      return (
        <g transform="rotate(-10)">
          <rect x={-22} y={-28} width={44} height={56} rx={4} fill="#1B5E20" />
          <rect x={-18} y={-24} width={36} height={48} rx={3} fill="none" stroke={C.gold} strokeWidth={2} />
          <path d={boteh(14, 20)} fill={C.gold} />
          <rect x={20} y={-27} width={4} height={54} fill="#FFF8E1" />
        </g>
      );
    case "held.santur":
      return (
        <g>
          <defs>
            <linearGradient id={`${u}-santur`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#A0522D" />
              <stop offset="1" stopColor="#6D3A1F" />
            </linearGradient>
          </defs>
          <path d={trapezoid(76, 40)} fill={`url(#${u}-santur)`} />
          {[0, 1, 2, 3, 4, 5].map((i) => {
            const w = 64 - i * 3;
            return <rect key={i} x={-w / 2} y={-14 + i * 5.5 - 0.4} width={w} height={0.8} fill="#E0E0E0" />;
          })}
          <circle cx={-16} cy={4} r={4.25} fill="none" stroke={C.gold} strokeWidth={1.5} />
          <circle cx={16} cy={4} r={4.25} fill="none" stroke={C.gold} strokeWidth={1.5} />
          {[-1, 1].map((side) => (
            <g key={side} transform={`translate(${side * 14} -43) rotate(${side * 30})`}>
              <g className="pm-mezrab" style={{ animationDelay: `${side > 0 ? -0.12 : 0}s` }}>
                <rect x={-1.5} y={0} width={3} height={26} rx={1.5} fill="#3E2723" />
              </g>
            </g>
          ))}
        </g>
      );
    default:
      return null;
  }
}

/** Where a held item sits next to Pashmak. */
export const heldOffset = (id: string) => (id === "held.santur" ? { x: -78, y: 118 } : { x: -92, y: 100 });

// ---------------------------------------------------------------- shop thumbnails

/** An item on its own (shop cards, collections, the Fāl reveal). 130×130 design space. */
export function ItemArtGroup({ item, u }: { item: Pick<ShopItem, "id" | "slot">; u: string }) {
  const slot: Slot = item.slot;
  switch (slot) {
    case "hat": {
      const dar = item.id === "hat.darvish";
      return (
        <g transform={`scale(${dar ? 1 : 1.1}) translate(0 ${dar ? 95 : 80})`}>
          <HatArt id={item.id} u={u} />
        </g>
      );
    }
    case "face":
      return (
        <g transform="scale(1.3) translate(0 4)">
          <GlassesArt id={item.id} u={u} />
        </g>
      );
    case "neck":
      return <ScarfArt id={item.id} u={u} big />;
    case "held":
      return (
        <g transform="scale(1.5)">
          <HeldArt id={item.id} u={u} />
        </g>
      );
    case "halo": {
      const t = haloTheme(item.id);
      return (
        <g>
          <defs>
            <radialGradient id={`${u}-ihalo`} gradientUnits="userSpaceOnUse" cx={0} cy={0} r={60}>
              <RadialStops colors={t.colors} start={0.08} />
            </radialGradient>
            <clipPath id={`${u}-iclip`}>
              <circle r={60} />
            </clipPath>
          </defs>
          <g clipPath={`url(#${u}-iclip)`}>
            <circle r={60} fill={`url(#${u}-ihalo)`} />
            <path d={khatam(0, 0, 40)} fill={t.center} />
            <g transform="scale(.5)">
              <HaloDecor theme={t} />
            </g>
          </g>
          <circle r={57.5} fill="none" stroke={t.rim} strokeWidth={5} />
        </g>
      );
    }
  }
}
